import React, { useEffect, useRef, useState } from "react"
import {
  ShoppingCartIcon,
  BookOpenIcon,
  HeartIcon,
  ClockIcon,
  ArrowRightStartOnRectangleIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import { CheckBadgeIcon } from "@heroicons/react/24/solid"
import { usePageContext } from "vike-react/usePageContext"
import { navigate } from "vike/client/router"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Value from "aldoo-ra/Value"
import { twMerge } from "tailwind-merge"
import Button from "aldoo-ra/Button"
import useT from "../Storefront/hooks/useT"
import { requestSignIn } from "../Storefront/utils/signIn"
import UserAvatar from "../Storefront/ui/UserAvatar"
import { orgLogoUrl } from "aldoo-ra/Organizations/api"
import { ListVendorOrgs } from "../AdminShared/api"
import { Thumb } from "../AdminShared/ui"
import { rememberedOrg, rememberOrg } from "../Vendor/currentOrg"

// the organizations this account belongs to, read once per account per page
// load (vendor {orgs}); a platform admin's view of every vendor is not "theirs"
const orgsByUser = new Map()
function useMyOrgs(userId) {
  const [orgs, setOrgs] = useState(() => (userId && orgsByUser.get(userId)) || null)
  useEffect(() => {
    if (!userId) return setOrgs(null)
    if (orgsByUser.has(userId)) return setOrgs(orgsByUser.get(userId))
    let alive = true
    ListVendorOrgs()
      .then((res) => {
        const mine = (res?.orgs || []).filter((o) => o.role && o.role !== "platform-admin")
        orgsByUser.set(userId, mine)
        if (alive) setOrgs(mine)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [userId])
  return orgs || []
}

/**
 * The organization this person works in (the one the merchant portal opened
 * last, or their only one): its logo, name and id, a link to its portal — and
 * the switch when they belong to more than one.
 */
function OrganizationCard({ userId, close }) {
  const t = useT()
  const orgs = useMyOrgs(userId)
  if (!orgs.length) return null
  const last = rememberedOrg(userId)
  const current = orgs.find((o) => o._id === last) || orgs[0]
  // the portal asks which one when nothing is remembered
  const switchOrg = () => {
    close?.()
    rememberOrg(userId, "")
    navigate("/vendor")
  }
  return (
    <>
      <hr className="border-bordered m-0" />
      <div className="flex flex-col gap-1" data-testid="menu-organization">
        <Button variant="none" href={`/vendor?org=${current._id}`} onClick={close} className="group flex items-center gap-3 rounded-lg px-3 py-2">
          <Thumb src={current.logo ? orgLogoUrl(current._id, current.logoUpdatedAt) : null} name={current.name || "?"} className="w-10 h-10 rounded-full text-sm shrink-0" />
          <span className="flex flex-col min-w-0 text-left">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="text-[15px] font-semibold text-text truncate">{current.name}</span>
              {current.verification === "approved" && <CheckBadgeIcon className="w-5 h-5 shrink-0 text-[#2196f3]" />}
            </span>
            {current.cid && <span className="text-[13px] text-text_secondary truncate">{current.cid}</span>}
          </span>
        </Button>
        {orgs.length > 1 && <MenuLink icon={ArrowsRightLeftIcon} label={t`Switch organization`} onClick={switchOrg} />}
        {/* one more company: the portal's application form, as its "Register another company" banner opens it */}
        <MenuLink to="/vendor?apply=new" icon={PlusIcon} label={t`New organization`} onClick={close} />
      </div>
    </>
  )
}


function MenuLink({ to, icon: Icon, label, onClick }) {
  const content = (
    <span className="flex items-center gap-2 text-[15px]">
      <Icon className="w-5 h-5" />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  )
  const cls = "flex w-full items-center rounded-lg px-3 py-2 text-text_secondary hover:text-text text-left transition-colors duration-700"
  if (to) {
    return (
      <Button variant="none" href={to} onClick={onClick} className={cls}>
        {content}
      </Button>
    )
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {content}
    </button>
  )
}

// Signed-in dropdown: profile card, orders, library, log out
export function ProfileMenuContent({ close, className = "" }) {
  const t = useT()
  const [profile] = Value("user-profile")
  const [auth] = PlatformAuth.useAuth()
  const logout = PlatformAuth.useLogout()

  // asked first, as on the profile page and in the merchant portal
  const onLogout = () => {
    close?.()
    showPromptDialog({
      title: t`Sign Out`,
      message: t`Are you sure you want to sign out?`,
      confirmText: t`Sign Out`,
      cancelText: t`Cancel`,
      onConfirm: async () => {
        await logout()
        setTimeout(() => navigate("/"), 0)
      },
    })
  }

  return (
    <div className={twMerge("flex flex-col gap-4 py-3", className)}>
      <Button 
        variant="none" 
        href="/user" 
        onClick={close} 
        className="group flex items-center gap-4 rounded-lg px-3 py-2"
      >
        <UserAvatar
          user={profile}
          size={44}
          className="!border-2 border-theme_white group-hover:border-theme_black transition-colors duration-300"
        />
        <div className="flex flex-col min-w-0">
          <span className="text-[16px] text-text truncate">
            {profile?.name}
          </span>
          <span className="text-[13px] text-text_secondary truncate">
            {profile?.email}
          </span>
        </div>
      </Button>
      <OrganizationCard userId={profile?._id || auth?._id} close={close} />
      <hr className="border-bordered m-0" />
      <MenuLink to="/user/orders" icon={ShoppingCartIcon} label={t`Orders`} onClick={close} />
      <hr className="border-bordered m-0" />
      <MenuLink to="/library" icon={BookOpenIcon} label={t`Library`} onClick={close} />
      <MenuLink to="/library/favorites" icon={HeartIcon} label={t`Favorites`} onClick={close} />
      <MenuLink to="/library/history" icon={ClockIcon} label={t`History`} onClick={close} />
      <hr className="border-bordered m-0" />
      <MenuLink icon={ArrowRightStartOnRectangleIcon} label={t`Sign Out`} onClick={onLogout} />
    </div>
  )
}

/**
 * Top-bar account control: "Sign In" pill for guests, avatar + dropdown
 * when signed in. Nothing renders until the auth store is read on the client.
 */
export default function ProfileButton({ className = "" }) {
  const t = useT()
  const [auth] = PlatformAuth.useAuth()
  const [profile] = Value("user-profile")
  const pageContext = usePageContext()
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const ref = useRef(null)

  useEffect(() => setMounted(true), [])
  useEffect(() => setOpen(false), [pageContext.urlPathname])
  // on a pointer device the menu opens on hover and closes a moment after the
  // pointer leaves (the gap to the menu must not close it); a tap still toggles
  const leaveTimer = useRef(null)
  const hoverOpen = () => {
    clearTimeout(leaveTimer.current)
    setOpen(true)
  }
  const hoverClose = () => {
    clearTimeout(leaveTimer.current)
    leaveTimer.current = setTimeout(() => setOpen(false), 200)
  }
  useEffect(() => () => clearTimeout(leaveTimer.current), [])
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [open])

  if (!mounted || auth === undefined) {
    return <span className={twMerge("inline-block h-[40px] w-[40px] rounded-full bg-white/10 animate-pulse", className)} aria-hidden="true" />
  }
  if (!auth) {
    return (
      <Button
        variant="primary_s"
        onClick={() => requestSignIn()}
        data-testid="navbar-signin"
        className={twMerge("whitespace-nowrap", className)}
      >
        {t`Sign In`}
      </Button>
    )
  }

  return (
    <div ref={ref} className={twMerge("relative flex items-center", className)} onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label={profile?.name || t`User`} aria-expanded={open} className="rounded-full p-1">
        {profile ? (
          <UserAvatar user={profile} size={40} data-testid="navbar-avatar" />
        ) : (
          <UserIcon className="w-8 h-8 text-white" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[500] pt-2 w-[360px] max-w-[calc(100vw-16px)]">
        <div className="rounded-lg bg-paper px-3 shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
          <ProfileMenuContent close={() => setOpen(false)} />
        </div>
        </div>
      )}
    </div>
  )
}
